import { useUserDirectory } from "@/hooks/use-user-directory";
import { useUser } from "@/components/UserProvider";
import { AppModal, Button, LoadingSpinner } from "@/components/ui";
import { createPersonnelIdentity } from "@/components/features/matter/information/matter-personnel-identity";
import { MatterPersonnelHistory } from "@/components/features/matter/information/MatterPersonnelHistory";
import { MatterPersonnelPeoplePicker } from "@/components/features/matter/information/MatterPersonnelPeoplePicker";
import { MatterPersonnelPeople } from "@/components/features/matter/information/MatterPersonnelPeople";
import {
  MatterPersonnelRemoveTeamDialog,
  MatterPersonnelTeams,
  MatterPersonnelTeamsPicker,
} from "@/components/features/matter/information/MatterPersonnelTeams";
import {
  planTeamRemoval,
  restoreTeamAssignments,
  teamRemovalMembers,
  withoutRemovedTeam,
} from "@/components/features/matter/information/matter-personnel-team-removal";
import {
  assignmentKey,
  mergePersonnelAssignments,
  type PersonnelAssignment,
} from "@/components/features/matter/information/matter-personnel-assignment-diff";
import {
  getActiveMatterTeamIds,
  getActivePersonnelRoster,
  getTeamMembersByTeamId,
  getTeamMemberIds,
  getTeamNamesByUserId,
  getPersonnelUserStatus,
} from "@/components/features/matter/information/MatterPersonnelTeamMembership";
import { useMatterPersonnelRoster } from "@/components/features/matter/information/useMatterPersonnelRoster";
import { useMatterPrimaryContact } from "@/components/features/matter/information/useMatterPrimaryContact";
import {
  getSuggestedRolesByUserId,
  getVisiblePersonnelAssignments,
  getInitialPersonnelAssignments,
  orderAssignedPeople,
} from "@/components/features/matter/information/matter-personnel-role-suggestions";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { getUsersOptions } from "@/lib/data/react-query";
import { useUpdateMatterPersonnel } from "@/lib/data/react-query/matters/matter-personnel";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
export function MatterPersonnelEditorContent({
  matter,
  close,
}: {
  matter: Matter;
  close: () => void;
}) {
  const { hasActionPermission } = useUser();
  const canEditTeams =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTeamConnection,
      ACTION_PERMISSION_VALUES.create,
    ) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTeamConnection,
      ACTION_PERMISSION_VALUES.delete,
    );
  const canEditPeople =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterAssignee,
      ACTION_PERMISSION_VALUES.create,
    ) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterAssignee,
      ACTION_PERMISSION_VALUES.delete,
    );
  const initialTeamIds = getActiveMatterTeamIds(matter);
  const initialAssignments = getInitialPersonnelAssignments(matter);
  const [teamIds, setTeamIds] = useState<string[]>(initialTeamIds);
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);
  const [removedByTeam, setRemovedByTeam] = useState<
    Record<string, PersonnelAssignment[]>
  >({});
  const [reselectedTeamIds, setReselectedTeamIds] = useState<string[]>([]);
  const [assignments, setAssignments] =
    useState<PersonnelAssignment[]>(initialAssignments);
  const [removedRosterUsers, setRemovedRosterUsers] = useState<string[]>([]);
  const [overriddenRoleUsers, setOverriddenRoleUsers] = useState<string[]>([]);
  const [lastRolesByUserId, setLastRolesByUserId] = useState<
    Partial<Record<string, string[]>>
  >({});
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);
  const [peoplePickerOpen, setPeoplePickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [teamSearch, setTeamSearch] = useState("");
  const { data: settings, isLoading: settingsLoading } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const roles = settings?.data.roles ?? [];
  const teams = settings?.data.teams ?? [];
  const { data: usersData } = useQuery(getUsersOptions());
  const disabledUserIds = new Set(
    (usersData?.data ?? [])
      .filter((user) => user.enabled === false)
      .map((user) => user.id),
  );
  const {
    newTeamIds,
    roster,
    rostersLoading,
    rostersErrored,
    retryRosters,
    rememberTeam,
  } = useMatterPersonnelRoster(initialTeamIds, teamIds);
  const statusFor = (userId: string) =>
    getPersonnelUserStatus(matter, roster, disabledUserIds, userId);
  const activeRoster = getActivePersonnelRoster(
    matter,
    teamIds,
    newTeamIds,
    reselectedTeamIds,
    roster,
  );
  const teamNamesByUserId = getTeamNamesByUserId(
    matter,
    teamIds,
    teamIds.map((teamId) => ({
      teamId,
      userIds: activeRoster
        .filter((teammate) => teammate.teamId === teamId)
        .map((teammate) => teammate.userId),
    })),
    teams,
  );
  const personnelUpdate = useUpdateMatterPersonnel();
  const uniqueAssignments = getVisiblePersonnelAssignments(
    activeRoster,
    assignments,
    removedRosterUsers,
    overriddenRoleUsers,
  );
  const selectedPeopleIds = [
    ...new Set(uniqueAssignments.map((assignee) => assignee.userId)),
  ];
  const primaryContact = useMatterPrimaryContact(matter, selectedPeopleIds);
  const suggestedRolesByUserId = getSuggestedRolesByUserId(
    activeRoster,
    initialAssignments,
    lastRolesByUserId,
  );
  const membersByTeamId = getTeamMembersByTeamId(
    matter,
    teamIds,
    activeRoster,
    uniqueAssignments,
    roles,
    disabledUserIds,
  );
  const directory = useUserDirectory({
    enabled: true,
    open: peoplePickerOpen,
    selectedIds: selectedPeopleIds,
  });
  const teamChanged =
    [...teamIds].sort().join() !== [...initialTeamIds].sort().join();
  const peopleChanged =
    [...uniqueAssignments.map(assignmentKey)].sort().join() !==
      [...initialAssignments.map(assignmentKey)].sort().join() ||
    removedRosterUsers.some((id) =>
      activeRoster.some((teammate) => teammate.userId === id),
    );
  const dirty =
    teamChanged ||
    peopleChanged ||
    primaryContact.changed ||
    reselectedTeamIds.length > 0;
  const hasMissingRole = uniqueAssignments.some((assignee) => !assignee.roleId);
  const { nameFor, profileFor } = createPersonnelIdentity(
    matter,
    roster,
    directory.users,
  );
  const orderedPeople = orderAssignedPeople(uniqueAssignments, nameFor);
  const rolesFor = (userId: string) =>
    uniqueAssignments
      .filter((item) => item.userId === userId)
      .map((item) => item.roleId);
  const addPerson = (userId: string, roleId?: string) => {
    setRemovedRosterUsers((current) => current.filter((id) => id !== userId));
    if (uniqueAssignments.some((assignee) => assignee.userId === userId))
      return;
    const selectedRoles = roleId
      ? [roleId]
      : (lastRolesByUserId[userId] ?? suggestedRolesByUserId.get(userId) ?? []);
    setOverriddenRoleUsers((current) => [...new Set([...current, userId])]);
    setAssignments((current) => [
      ...current,
      ...(selectedRoles.length
        ? selectedRoles
        : [roles.length === 1 ? (roles[0]?.id ?? "") : ""]
      ).map((selectedRoleId) => ({ userId, roleId: selectedRoleId })),
    ]);
  };
  const removePerson = (userId: string) => {
    const roleIds = rolesFor(userId);
    if (roleIds.length)
      setLastRolesByUserId((current) => ({ ...current, [userId]: roleIds }));
    setAssignments((current) =>
      current.filter((item) => item.userId !== userId),
    );
    setRemovedRosterUsers((current) => [...new Set([...current, userId])]);
    setOverriddenRoleUsers((current) => [...new Set([...current, userId])]);
  };
  const changeRoles = (userId: string, roleIds: string[]) => {
    if (!roleIds.length) {
      removePerson(userId);
      return;
    }
    setOverriddenRoleUsers((current) => [...new Set([...current, userId])]);
    setAssignments((current) => [
      ...current.filter((item) => item.userId !== userId),
      ...[...new Set(roleIds)].map((roleId) => ({ userId, roleId })),
    ]);
  };
  const membersForTeam = (teamId: string) => {
    const newMembers = roster
      .filter((item) => item.teamId === teamId)
      .map((item) => ({ userId: item.userId, roleId: item.roleId }));
    return teamRemovalMembers(matter, teamId, newMembers);
  };
  const removeTeam = (teamId: string, removeAssignees: boolean) => {
    const removeMembers =
      removeAssignees || (!canEditPeople && !initialTeamIds.includes(teamId));
    const result = planTeamRemoval({
      matter,
      teamId,
      initial: initialAssignments,
      edited: assignments,
      members: membersForTeam(teamId),
      selectedTeamIds: teamIds,
      roster,
      removeAssignees: removeMembers,
    });
    setRemovedByTeam((current) => ({ ...current, [teamId]: result.removed }));
    setAssignments(result.assignments);
    setTeamIds((current) => current.filter((id) => id !== teamId));
    setReselectedTeamIds((current) => current.filter((id) => id !== teamId));
    setPendingTeamId(null);
  };
  const addTeam = async (teamId: string) => {
    const reselected =
      initialTeamIds.includes(teamId) &&
      Object.prototype.hasOwnProperty.call(removedByTeam, teamId);
    const restored = [
      ...(removedByTeam[teamId] ?? []),
      ...(reselected ? teamRemovalMembers(matter, teamId, []) : []),
    ];
    if (reselected)
      setReselectedTeamIds((current) => [...new Set([...current, teamId])]);
    setAssignments((current) => restoreTeamAssignments(current, restored));
    setTeamIds((current) => [...new Set([...current, teamId])]);
    setRemovedByTeam((current) => withoutRemovedTeam(current, teamId));
    const memberIds = getTeamMemberIds(matter, teamId, activeRoster, restored);
    setRemovedRosterUsers((current) =>
      current.filter((id) => !memberIds.has(id)),
    );
    try {
      const members = await rememberTeam(teamId);
      setRemovedRosterUsers((current) =>
        current.filter((id) => !members.some((member) => member.userId === id)),
      );
    } catch {
      // The roster error is shown in Personnel; team assignment is still staged.
    }
  };
  const save = async () => {
    if (
      isSaving ||
      hasMissingRole ||
      settingsLoading ||
      rostersLoading ||
      rostersErrored
    )
      return;
    setIsSaving(true);
    try {
      if (teamChanged) {
        const currentTeamIds = await personnelUpdate.getCurrentTeamIds(
          matter.id,
        );
        const removedTeamIds = new Set(
          initialTeamIds.filter((id) => !teamIds.includes(id)),
        );
        const addedTeamIds = teamIds.filter(
          (id) => !initialTeamIds.includes(id),
        );
        await personnelUpdate.updateTeams({
          matterId: matter.id,
          teamIds: [
            ...new Set([
              ...currentTeamIds.filter((id) => !removedTeamIds.has(id)),
              ...addedTeamIds,
            ]),
          ],
        });
      }
      if (
        canEditPeople &&
        (peopleChanged ||
          primaryContact.changed ||
          teamChanged ||
          reselectedTeamIds.length > 0)
      ) {
        const current = await personnelUpdate.getCurrentAssignments(matter.id);
        await personnelUpdate.updateAssignees({
          matterId: matter.id,
          assignees: mergePersonnelAssignments({
            initial: initialAssignments,
            edited: uniqueAssignments,
            current,
            removedUserIds: removedRosterUsers,
            primaryUserId: primaryContact.primaryUserId,
          }),
        });
      }
      await personnelUpdate.refreshMatter(matter.id);
      toast.success("Personnel updated");
      close();
    } catch {
      await personnelUpdate.refreshMatter(matter.id).catch(() => undefined);
      toast.error(
        "Could not save all personnel changes. Review the matter and try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <AppModal
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title="Personnel"
        size="4xl"
        className="h-[min(85dvh,680px)]"
        isDirty={dirty}
        loading={isSaving || settingsLoading}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={() => void save()}
              disabled={
                !dirty ||
                isSaving ||
                hasMissingRole ||
                settingsLoading ||
                rostersLoading ||
                rostersErrored
              }
            >
              Save changes
            </Button>
          </div>
        }
      >
        <div className="w-full space-y-5">
          <MatterPersonnelTeams
            matter={matter}
            teams={teams}
            teamIds={teamIds}
            membersByTeamId={membersByTeamId}
            canEditTeams={canEditTeams}
            onEdit={() => {
              setTeamPickerOpen(true);
            }}
            onRequestRemoval={setPendingTeamId}
          />
          <MatterPersonnelPeople
            people={orderedPeople}
            roles={roles}
            rolesFor={rolesFor}
            nameFor={nameFor}
            statusFor={statusFor}
            profileFor={profileFor}
            teamNamesByUserId={teamNamesByUserId}
            canEdit={canEditPeople}
            primaryUserId={primaryContact.primaryUserId}
            onPrimaryContactChange={primaryContact.setPrimaryUserId}
            onChangeRoles={changeRoles}
            onRemove={removePerson}
            onEdit={() => {
              setPeoplePickerOpen(true);
            }}
          />
          {rostersLoading && (
            <p
              className="text-muted-foreground flex items-center gap-2 text-sm"
              role="status"
            >
              <LoadingSpinner className="h-4 w-4" />
              Loading team members...
            </p>
          )}
          {rostersErrored && (
            <div className="flex items-center gap-2 text-sm" role="alert">
              <span>Team members could not be loaded.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void retryRosters()}
              >
                Retry
              </Button>
            </div>
          )}
          <MatterPersonnelHistory matter={matter} statusFor={statusFor} />
        </div>
        <MatterPersonnelRemoveTeamDialog
          matter={matter}
          teams={teams}
          teamId={pendingTeamId}
          canEditPeople={canEditPeople}
          onClose={() => {
            setPendingTeamId(null);
          }}
          onRemove={(removeAssignees) => {
            if (pendingTeamId) removeTeam(pendingTeamId, removeAssignees);
          }}
        />
      </AppModal>
      <AppModal
        open={teamPickerOpen}
        onOpenChange={(open) => {
          if (!open) setTeamPickerOpen(false);
        }}
        title="Edit teams"
        size="lg"
        className="!z-[103] h-[min(70dvh,500px)]"
        overlayClassName="!z-[102] backdrop-blur-[5px]"
        bodyContained
        footer={
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setTeamPickerOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        }
      >
        <MatterPersonnelTeamsPicker
          teams={teams}
          open={teamPickerOpen}
          selectedIds={teamIds}
          disabledUserIds={disabledUserIds}
          search={teamSearch}
          onSearchChange={setTeamSearch}
          onAdd={addTeam}
          onRemove={(teamId) => {
            setTeamPickerOpen(false);
            setPendingTeamId(teamId);
          }}
        />
      </AppModal>
      <AppModal
        open={peoplePickerOpen}
        onOpenChange={(open) => {
          if (!open) setPeoplePickerOpen(false);
        }}
        title="Add people"
        size="2xl"
        className="!z-[103] h-[min(80dvh,600px)]"
        overlayClassName="!z-[102] backdrop-blur-[5px]"
        bodyContained
        footer={
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setPeoplePickerOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        }
      >
        <MatterPersonnelPeoplePicker
          directory={directory}
          selectedIds={selectedPeopleIds}
          statusFor={statusFor}
          suggestedRolesByUserId={suggestedRolesByUserId}
          assignments={uniqueAssignments}
          roles={roles}
          profileFor={profileFor}
          onAdd={addPerson}
          onRolesChange={changeRoles}
          onRemove={removePerson}
        />
      </AppModal>
    </>
  );
}
